import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { I18nProvider } from "./contexts/I18nContext";
import Layout from "./components/Layout";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Fleet from "./pages/Fleet";
import AircraftDetail from "./pages/AircraftDetail";
import CalendarPage from "./pages/Calendar";
import Logbook from "./pages/Logbook";
import Settlements from "./pages/Settlements";
import Reserves from "./pages/Reserves";
import Compliance from "./pages/Compliance";
import Documents from "./pages/Documents";
import Maintenance from "./pages/Maintenance";
import FleetMaintenance from "./pages/FleetMaintenance";
import Reconciliation from "./pages/Reconciliation";
import Audit from "./pages/Audit";
import Participants from "./pages/Participants";
import Settings from "./pages/Settings";
import Billing from "./pages/Billing";
import Airworthiness from "./pages/Airworthiness";
import Signup from "./pages/Signup";
import Superuser, { SuperuserTenantDetail } from "./pages/Superuser";
import SuperuserTypeDetail from "./pages/SuperuserTypeDetail";
import UpgradePrompt from "./components/UpgradePrompt";
import "@/App.css";

function ProtectedShell({ ownerOnly = false, superuserOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-500 font-mono text-xs uppercase tracking-[0.3em]">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  const isAdmin = user.role === "tenant_admin" || user.role === "owner" || user.platform_superuser;
  if (superuserOnly && !user.platform_superuser) return <Navigate to="/" replace />;
  if (ownerOnly && !isAdmin) return <Navigate to="/calendar" replace />;
  return <Layout />;
}

/**
 * ProGate — Phase 4C. Renders an Upgrade screen when the authenticated user's
 * tenant doesn't have cost-sharing access (Standard plan), else renders the
 * gated page. Used to wrap Settlements / Reserves / Reconciliation route
 * elements.
 */
function ProGate({ feature, children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  const ok = !!user?.has_cost_sharing_access || !!user?.platform_superuser;
  if (!ok) return <UpgradePrompt feature={feature} />;
  return children;
}

/**
 * `/` (root) — Landing page for visitors, smart redirect for authenticated users.
 * Authenticated admin → /dashboard · Authenticated participant → /calendar.
 */
function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Landing />;
  const isAdmin = user.role === "tenant_admin" || user.role === "owner" || user.platform_superuser;
  return <Navigate to={isAdmin ? "/dashboard" : "/calendar"} replace />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route element={<ProtectedShell />}>
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/logbook" element={<Logbook />} />
        <Route path="/settlements" element={<ProGate feature="Settlements"><Settlements /></ProGate>} />
        <Route path="/reserves" element={<ProGate feature="Reserves"><Reserves /></ProGate>} />
        <Route path="/compliance" element={<Compliance />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/maintenance" element={<Maintenance />} />
        <Route path="/maintenance/fleet" element={<FleetMaintenance />} />
        <Route path="/reconciliation" element={<ProGate feature="Reconciliation"><Reconciliation /></ProGate>} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/airworthiness" element={<Airworthiness />} />
      </Route>
      <Route element={<ProtectedShell ownerOnly />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/fleet" element={<Fleet />} />
        <Route path="/fleet/:id" element={<AircraftDetail />} />
        <Route path="/audit" element={<Audit />} />
        <Route path="/participants" element={<Participants />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
      <Route element={<ProtectedShell superuserOnly />}>
        <Route path="/superuser" element={<Superuser />} />
        <Route path="/superuser/tenants/:tid" element={<SuperuserTenantDetail />} />
        <Route path="/superuser/aircraft-types/:slug" element={<SuperuserTypeDetail />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <I18nProvider>
            <AppRouter />
          </I18nProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
